/**
 * @file ColorField.tsx
 * @description Общее поле цвета конструктора (phase19.16, партия SB120 —
 * «владелец разрабатывает дизайн сам»; uiux-sb120-fields §3). Роли темы —
 * быстрый выбор, «Свой цвет» — любой цвет через окно `ColorDialog`
 * (оттенок, код, пипетка, «Последние»). Слабый контраст — предупреждение с
 * числом и «Взять читаемый», не запрет.
 *
 * Два режима (`mode`), модель — `lib/site-fields/color-field-model.ts`:
 *  - text — «Авто» → роли → «Свой цвет»; контраст с фоном элемента;
 *  - fill — («Без фона») → роли-заливки → «Свой цвет»; вместе с заливкой
 *    отдаётся тон поверхности (`resolveSurfaceFill(...).tone`).
 * Подпись над полем со «↺ <inheritLabel>» справа; если подпись уже у группы —
 * `hideLabel` (имя группы радиокнопок остаётся для чтеца экрана).
 */

import type { SiteColorRole } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ColorContrastNote } from "./ColorContrastNote";
import { ColorDialog } from "./ColorDialog";
import { ColorSwatchGrid } from "./ColorSwatchGrid";
import { fillFieldModel, textFieldModel, type ColorFieldProps } from "../../lib/site-fields/color-field-model";

export type { ColorFieldFillProps, ColorFieldProps, ColorFieldTextProps, ColorTextValue } from "../../lib/site-fields/color-field-model";

/** Пояснение окна «Свой цвет» по режиму. */
const DIALOG_HINT: Readonly<Record<ColorFieldProps<SiteColorRole>["mode"], string>> = {
  text: "Любой цвет. Слабый контраст — предупреждение, выбор за вами.",
  fill: "Любой цвет. Текст на нём платформа подберёт светлый или тёмный.",
};

export function ColorField<R extends SiteColorRole>(props: ColorFieldProps<R>) {
  const { label, hideLabel = false, colors, onReset, inheritLabel = "авто", custom = true, contrast = true, mode } = props;
  const [picking, setPicking] = useState<string | null>(null);
  const model = props.mode === "text" ? textFieldModel(props) : fillFieldModel(props);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {!hideLabel || onReset ? (
        <div className="flex min-w-0 items-center justify-between gap-2">
          {hideLabel ? <span /> : <Label>{label}</Label>}
          {onReset ? (
            <Button variant="ghost" size="sm" className="min-h-12 can-hover:min-h-8" onClick={onReset} aria-label={`${label}: ${inheritLabel}`}>
              <RotateCcw aria-hidden />
              {inheritLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      <ColorSwatchGrid
        label={label}
        cells={model.cells}
        custom={custom ? { color: model.customColor, onOpen: () => setPicking(model.customColor ?? model.seed) } : undefined}
      />
      {contrast ? <ColorContrastNote mode={mode} ratio={model.ratio} photo={model.photo} readable={model.readable} /> : null}
      {custom ? (
        <ColorDialog
          initial={picking}
          title={`${label}: свой цвет`}
          description={DIALOG_HINT[mode]}
          mode={mode}
          colors={colors}
          sampleBg={model.sampleBg}
          onClose={() => setPicking(null)}
          onDone={(color) => {
            setPicking(null);
            model.setCustom(color);
          }}
        />
      ) : null}
    </div>
  );
}
